import { useEffect, useMemo, useRef, useState } from 'react'

import type {
    StockOutItemInput,
    StockOutMaterialItemInput,
} from '@/lib/api/sales-order-api'
import type {
    LotLine,
    MatLine,
} from '@/components/sections/lot-allocation-editor'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import {
    LotAllocationEditor,
    hasAnyError,
    hasMatError,
    linesToStockOutItems,
    matLinesToInputs,
    seedLotLine,
    seedMatLine,
} from '@/components/sections/lot-allocation-editor'
import {
    StockOutShortageAlert,
    computeStockOutShortages,
} from '@/components/sections/stock-out-shortage-alert'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useStockOutSuggestion } from '@/hooks/use-sales-order-query'
import { formatApiError } from '@/lib/format-api-error'
import { workflowErrorCode } from '@/lib/payment-collection-tracking'
import { m } from '@/lib/paraglide/messages'

interface StockOutDialogProps {
    workflow?: ReturnType<typeof useWorkflowTracking>
    orderId: string
    open: boolean
    onOpenChange: (open: boolean) => void
    onSubmit: (
        items: StockOutItemInput[],
        materialItems: StockOutMaterialItemInput[],
    ) => void
    submitting?: boolean
}

// Popup "Tạo phiếu xuất kho": seed lô gợi ý (FIFO) của đơn vào editor chọn lô
// dùng chung (LotAllocationEditor — POS cũng dùng), nhân viên chỉnh rồi submit.
export function StockOutDialog({
    orderId,
    open,
    onOpenChange,
    onSubmit,
    submitting,
    workflow: parentWorkflow,
}: StockOutDialogProps) {
    const localWorkflow = useWorkflowTracking('order_stock_out', {
        active: open && !parentWorkflow,
        attemptKey: orderId,
    })
    const workflow = parentWorkflow ?? localWorkflow
    const {
        data: suggestion,
        isLoading,
        error: suggestionError,
    } = useStockOutSuggestion(orderId, open)
    // Thiếu hàng tính trên gợi ý của server (tồn thật), không theo chỉnh tay.
    const shortages = useMemo(
        () => computeStockOutShortages(suggestion),
        [suggestion],
    )
    const [lines, setLines] = useState<LotLine[]>([])
    const [matLines, setMatLines] = useState<MatLine[]>([])
    // Seed lines đúng MỘT LẦN mỗi lần mở — nếu suggestion refetch (reconnect,
    // invalidation) trong lúc đang mở, không được ghi đè chỉnh sửa tay của user.
    const seeded = useRef(false)

    useEffect(() => {
        if (!open) {
            seeded.current = false
            setBlockedMsg(null)
            return
        }
        if (!suggestion || seeded.current) return
        seeded.current = true
        setLines(suggestion.items.map((it) => seedLotLine(it)))
        setMatLines((suggestion.materials ?? []).map((mt) => seedMatLine(mt)))
    }, [open, suggestion])

    const selectedLineCount =
        lines.filter((line) => Boolean(line.productId)).length +
        matLines.filter((line) => Boolean(line.materialId)).length
    useEffect(() => {
        if (open && !isLoading)
            workflow.setContext({ lineCount: selectedLineCount })
    }, [open, isLoading, workflow, selectedLineCount])
    // Đo số lần popup mở khi kho thiếu hàng (khung cảnh báo hiện) và kết cục.
    useEffect(() => {
        if (open && suggestion)
            workflow.setContext({ shortageLineCount: shortages.length })
    }, [open, suggestion, workflow, shortages.length])

    const isEmpty = lines.length === 0 && matLines.length === 0
    // KHÔNG disable submit vì lỗi validate (AGENTS.md → Form Submit & Validation):
    // người dùng phải bấm được để BIẾT vì sao bị chặn. Chỉ khoá khi đang gửi
    // (chống double-submit) hoặc chưa có gì để xuất.
    const disabled = isLoading || isEmpty || Boolean(submitting)
    const [blockedMsg, setBlockedMsg] = useState<string | null>(null)
    // Server từ chối gợi ý lô (vd KIOTVIET_ITEMS_UNLINKED — đơn còn dòng chưa
    // nối, M-145): ghi api_error MỘT lần mỗi lần mở để đếm số lần xuất kho bị
    // chặn. Chỉ mã lỗi, không gửi mã hàng.
    const reportedError = useRef<unknown>(null)
    useEffect(() => {
        if (!open) {
            reportedError.current = null
            return
        }
        if (!suggestionError || reportedError.current === suggestionError)
            return
        reportedError.current = suggestionError
        workflow.step('api_error', {
            errorCode: workflowErrorCode(suggestionError),
        })
    }, [open, suggestionError, workflow])

    const handleSubmit = () => {
        workflow.setContext({ lineCount: selectedLineCount })
        if (hasAnyError(lines) || hasMatError(matLines) || isEmpty) {
            workflow.step('validation_failed')
            setBlockedMsg(m.stock_out_submit_blocked())
            return
        }
        setBlockedMsg(null)
        onSubmit(linesToStockOutItems(lines), matLinesToInputs(matLines))
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{m.stock_out_dialog_title()}</DialogTitle>
                    <DialogDescription>
                        {m.stock_out_dialog_desc()}
                    </DialogDescription>
                </DialogHeader>

                {isLoading ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                        {m.stock_out_dialog_loading()}
                    </p>
                ) : suggestionError ? (
                    // Server từ chối gợi ý (vd đơn KiotViet còn dòng chưa nối
                    // sản phẩm — M-145): nói đúng lý do để người dùng xử lý.
                    <p
                        role="alert"
                        className="py-6 text-center text-sm text-destructive"
                    >
                        {
                            formatApiError(
                                suggestionError,
                                m.stock_out_suggestion_error(),
                            ).title
                        }
                    </p>
                ) : isEmpty ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                        {m.stock_out_empty()}
                    </p>
                ) : (
                    <>
                        <StockOutShortageAlert shortages={shortages} />
                        <LotAllocationEditor
                            workflow={workflow}
                            lines={lines}
                            matLines={matLines}
                            warehouseId={suggestion?.warehouse_id}
                            onLinesChange={(value) => {
                                workflow.interact('items')
                                setLines(value)
                            }}
                            onMatLinesChange={(value) => {
                                workflow.interact('items')
                                setMatLines(value)
                            }}
                        />
                    </>
                )}

                {blockedMsg ? (
                    <p className="text-sm text-destructive">{blockedMsg}</p>
                ) : null}

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                    >
                        {m.stock_out_dialog_cancel()}
                    </Button>
                    <Button
                        type="button"
                        disabled={disabled}
                        onClick={handleSubmit}
                    >
                        {m.stock_out_dialog_submit()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
